import React, {useEffect, useRef} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import type {EmailOtpType} from '@supabase/supabase-js';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {confirmEmailLink} from '../store/slices/authThunks';
import {
  authLoadingSelector,
  confirmEmailLinkApiSelector,
  isAuthenticatedSelector,
} from '../store/selectors/auth.selectors';
import AuthCard from './auth/AuthCard';
import {primaryButtonClass} from './formStyles';

// Everything `verifyOtp`'s token_hash path accepts. Only `recovery` is branched on below; the rest
// (in practice `magiclink`, from the signup login link) all mean "you're in".
const KNOWN_TYPES: EmailOtpType[] = ['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'email'];

/**
 * `?next=` is honoured if the email template sends one — that is the convention in Supabase's own
 * examples — but only as a **relative in-app path**. A protocol-relative `//evil.com` is still a
 * valid `Location` value, so rejecting anything past a single leading slash is what stops an emailed
 * link from being turned into an open redirect.
 */
function safeNextPath(next: string | null): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//')) {
    return null;
  }
  return next;
}

/**
 * The landing point for every emailed link that proves identity by token, rather than by the
 * OAuth/PKCE `code` that `detectSessionInUrl` exchanges on its own: the signup login link
 * (`type=magiclink`, sent by provisioning) and the password-recovery link (`type=recovery`). Those
 * templates link here with `token_hash` and `type` in the query string, and **nothing happens
 * automatically for that shape** — this container is what calls
 * `supabase.auth.verifyOtp({token_hash, type})`.
 *
 * Recovery goes on to `/reset-password` to choose the new password; everything else goes into the
 * app, where the `password_set` gate sends a checkout-provisioned account to `/set-password` by
 * itself. This screen only has to establish the session, not know about that gate.
 *
 * **It also tolerates arriving with no `token_hash` but a live session.** That is what a stock
 * `{{ .ConfirmationURL }}` template produces if it is pointed here: Supabase verifies server-side and
 * redirects with tokens `detectSessionInUrl` has already consumed by now. Treating that as an invalid
 * link would strand a user who is, in fact, signed in.
 */
const AuthConfirmContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const api = useAppSelector(confirmEmailLinkApiSelector);
  const authLoading = useAppSelector(authLoadingSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);

  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type');
  const next = safeNextPath(searchParams.get('next'));

  const isKnownType = type !== null && KNOWN_TYPES.includes(type as EmailOtpType);
  const destination = next ?? (type === 'recovery' ? '/reset-password' : '/welcome');

  // StrictMode double-invokes effects, and a token_hash cannot be redeemed twice — the second call
  // would fail server-side and overwrite the first attempt's success with an error.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!tokenHash || !isKnownType || startedRef.current) {
      return;
    }
    startedRef.current = true;

    dispatch(confirmEmailLink({tokenHash, type: type as EmailOtpType})).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        navigate(destination, {replace: true});
      }
    });
  }, [dispatch, navigate, tokenHash, type, isKnownType, destination]);

  // No token to verify, but a session landed anyway: the link was the ConfirmationURL shape and
  // `detectSessionInUrl` has already done the work. Wait for session restore to settle first —
  // before that, "not authenticated" only means "not yet known".
  useEffect(() => {
    if (!tokenHash && !authLoading && isAuthenticated) {
      navigate(destination, {replace: true});
    }
  }, [tokenHash, authLoading, isAuthenticated, navigate, destination]);

  const linkIsUnusable = (!tokenHash && !authLoading && !isAuthenticated)
    || (tokenHash !== null && !isKnownType);

  if (linkIsUnusable || api.status === 'rejected') {
    return (
      <AuthCard
        title="This link is invalid or has expired"
        subtitle={api.error || 'Request a new one and try again.'}
        footer={
          <Link to="/forgot-password" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                        Reset your password instead
          </Link>
        }
      >
        <Link to="/login" className={`mt-5 block w-full text-center ${primaryButtonClass}`}>
                    Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-950">
      <div className="flex items-center gap-2.5 text-sm text-gray-400">
        <span className="h-4 w-4 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
                Signing you in…
      </div>
    </div>
  );
};

export default AuthConfirmContainer;
